import { SuggestedCommodityCodeDto } from '@billing/app/api'
import { useBillingServiceStore } from '@billing/AppProvider'
import { CommodityFormHeader } from '@billing/features/commodity/components/CommodityFormHeader'
import { CommodityFormMapper } from '@billing/features/commodity/components/form/commodity-form.mapper'
import { CommodityFormProfile } from '@billing/features/commodity/components/form/commodity-form.profile'
import { CommodityForm } from '@billing/features/commodity/components/form/CommodityForm'
import { useOperationEquipmentMessageHandler } from '@billing/features/commodity/hooks/useCommodityMessageHandler'
import commodityService from '@billing/services/CommodityService'
import { getPackageName } from '@billing/utils/helper'
import { Box, Container } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { PATHS, useAsyncFetch, withAuthenticationRequired } from '@tom-ui/utils'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

export const CommodityFormPage = withAuthenticationRequired(
  observer(() => {
    const theme = useMinimalsTheme()
    const { t } = useTranslate()
    const navigate = useNavigate()
    const { appStore, commodityStore, packageStore } = useBillingServiceStore()
    const { getExceptionMessage } = useOperationEquipmentMessageHandler()
    const { id, parent, catalogue } = useParams<{
      id: string
      parent: string
      catalogue: string
    }>()
    const [suggestedCode, setSuggestedCode] = useState<SuggestedCommodityCodeDto>()

    const formId = 'commodityForm'
    const commodityId = id ? Number(id) : undefined
    const commodityParentId = parent ? Number(parent) : undefined
    const commodityCatalogueId = catalogue ? Number(catalogue) : undefined

    useAsyncFetch(async () => {
      const requests = [
        packageStore.load(),
        commodityService
          .getSuggestedCode(commodityCatalogueId, commodityParentId)
          .then(code => setSuggestedCode(code)),
      ]

      if (commodityId) requests.push(commodityStore.loadById(commodityId))

      await Promise.all(requests)
    }, [commodityCatalogueId, commodityId, commodityParentId, commodityStore, packageStore])

    const commodity = computed(() =>
      commodityId ? commodityStore.items.find(x => x.id === commodityId) : undefined,
    ).get()

    const handleSubmit = async (formValues: CommodityFormProfile) => {
      try {
        if (!commodityId) {
          await commodityStore.add(CommodityFormMapper.mapFormValuesToCreateDto(formValues))
          appStore.setShowAlert(
            'success',
            t('commodityAdded', 'Commodity has been added successfully.'),
          )
        } else {
          await commodityStore.update(CommodityFormMapper.mapFormValuesToUpdateDto(formValues))
          appStore.setShowAlert(
            'success',
            t('commodityUpdated', 'Commodity has been updated successfully.'),
          )
        }

        navigate(PATHS.masterData.commoditiesAndPackaging.url)
      } catch (error) {
        appStore.setShowAlert('error', getExceptionMessage(error))
      }
    }

    return (
      <Box
        sx={{
          bgcolor: theme.palette.grey[100],
          minHeight: '100%',
        }}
      >
        <CommodityFormHeader id={id} formId={formId} suggestedCode={suggestedCode} />

        <Container sx={{ paddingY: 2 }}>
          <CommodityForm
            id={formId}
            commodity={commodity}
            suggestedCode={suggestedCode}
            catalogueId={commodityCatalogueId}
            parentCommodityId={commodityParentId}
            packageOptions={packageStore.items.map(x => ({
              value: x.id,
              label: getPackageName(x),
            }))}
            onSubmit={(formValues: CommodityFormProfile) => handleSubmit(formValues)}
          />
        </Container>
      </Box>
    )
  }),
)
